import IAddress from "../../../models/IAddress";
import INewAddress from "../../../models/INewAddress";
import OwLabel from "../OwLabel/OwLabel";
import "./DeliveryWindowsDisplay.scss";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

type Props = {
    address: IAddress | INewAddress;
    showTitle?: boolean;
    compact?: boolean;
    className?: string;
};

const formatTime = (time: string): string => {
    if (!time) return "";
    // Convert 24h to 12h format
    const [hours, minutes] = time.split(":");
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
};

export const DeliveryWindowsDisplay = (props: Props) => {
    const { address, showTitle = true, compact = false, className = "" } = props;

    // Check if we have day-specific windows
    const addressWithWindows = address as IAddress;
    const hasDaySpecificWindows = addressWithWindows.Address_Delivery_Windows && addressWithWindows.Address_Delivery_Windows.length > 0;

    // Check if we have all-days window
    const hasAllDaysWindow = address.delivery_window_start && address.delivery_window_end;

    // No delivery windows configured
    if (!hasDaySpecificWindows && !hasAllDaysWindow) {
        return null;
    }

    const renderConstraintBadge = (isHard: boolean | undefined, penalty?: number) => {
        if (isHard === false) {
            return <span className="badge bg-warning text-dark ms-2">Soft {penalty ? `(${penalty})` : ""}</span>;
        }
        return <span className="badge bg-secondary ms-2">Hard</span>;
    };

    const renderAllDaysWindow = () => (
        <div className="all-days-display">
            <div className="d-flex align-items-center">
                <i className="fad fa-clock text-muted me-2" />
                <span className="time-range">
                    {formatTime(address.delivery_window_start!)} - {formatTime(address.delivery_window_end!)}
                </span>
                {renderConstraintBadge(address.delivery_window_is_hard, address.delivery_window_soft_penalty_per_hour)}
            </div>
            <div className="text-muted small mt-1">All days</div>
        </div>
    );

    const renderDaySpecificWindows = () => {
        const windows = addressWithWindows.Address_Delivery_Windows!;
        // Sort by day of week
        const sortedWindows = [...windows].sort((a, b) => a.day_of_week - b.day_of_week);

        if (compact) {
            // Compact view: show abbreviated list
            return (
                <div className="day-specific-display-compact">
                    <div className="d-flex align-items-center flex-wrap">
                        <i className="fad fa-clock text-muted me-2" />
                        {sortedWindows.map((w) => (
                            <span key={w.day_of_week} className="day-badge me-1 mb-1">
                                <span className="day-name">{DAY_LABELS[w.day_of_week]}</span>
                                <span className="day-time ms-1">
                                    {formatTime(w.start_time)}-{formatTime(w.end_time)}
                                </span>
                            </span>
                        ))}
                    </div>
                </div>
            );
        }

        // Full view: show table-like layout
        return (
            <div className="day-specific-display">
                {sortedWindows.map((w) => (
                    <div key={w.day_of_week} className="day-row d-flex align-items-center mb-1">
                        <span className="day-name" style={{ width: "50px" }}>
                            {DAY_LABELS[w.day_of_week]}
                        </span>
                        <span className="time-range ms-2">
                            {formatTime(w.start_time)} - {formatTime(w.end_time)}
                        </span>
                        {renderConstraintBadge(w.is_hard, w.soft_penalty_per_hour)}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className={`DeliveryWindowsDisplay ${className}`}>
            {showTitle && <OwLabel title="delivery_windows" leftIconClass="fad fa-truck-clock" className="mb-1" noBottomMargin={true} />}
            <div className="delivery-windows-content">{hasDaySpecificWindows ? renderDaySpecificWindows() : renderAllDaysWindow()}</div>
        </div>
    );
};

export default DeliveryWindowsDisplay;
